import React from 'react';
import { Checkbox } from '@roger/ui-components';
import type { Checklist } from './useChecklist';

/**
 * Where the list opens from. A column header's filter has bold labels and a
 * roomier last row; a grid cell's list is drawn plainer, like the cells.
 */
export type ChecklistVariant = 'header' | 'cell';

/**
 * The open list: the options, grouped under headings, then "Clear all", then
 * an action row when there is one.
 */
export function ChecklistList({
  checklist,
  ariaLabel,
  variant,
}: {
  checklist: Checklist;
  ariaLabel: string;
  variant: ChecklistVariant;
}) {
  const { options, value, active, setActive, itemId, clear, footer } =
    checklist;
  return (
    <div
      ref={checklist.listRef}
      role='listbox'
      aria-label={ariaLabel}
      aria-multiselectable={!checklist.single}
      aria-activedescendant={checklist.activeId}
      tabIndex={-1}
      onKeyDown={checklist.onListKeyDown}
      className='max-h-72 overflow-y-auto outline-none'
    >
      {options.map((option, index) => {
        const checked = value.includes(option.value);
        const heading =
          option.group && options[index - 1]?.group !== option.group
            ? option.group
            : null;
        return (
          <React.Fragment key={option.value}>
            {heading && (
              <div className='px-2 pb-0.5 pt-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground'>
                {heading}
              </div>
            )}
            <div
              id={itemId(index)}
              role='option'
              aria-selected={checked}
              data-index={index}
              onMouseEnter={() => setActive(index)}
              // Keeps focus on the list, so the keys go on working there.
              onMouseDown={e => e.preventDefault()}
              onClick={() => checklist.toggle(option)}
              className={`flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1 text-xs ${index === active ? 'bg-muted' : ''}`}
            >
              <Checkbox
                checkboxSize='sm'
                checked={checked}
                tabIndex={-1}
                className='pointer-events-none'
              />
              <span className={variant === 'header' ? 'font-medium' : ''}>
                {option.label}
              </span>
              {option.hint && (
                <span className='text-xs text-muted-foreground'>
                  {option.hint}
                </span>
              )}
            </div>
          </React.Fragment>
        );
      })}
      {clear && (
        <button
          id={itemId(clear.index)}
          type='button'
          data-index={clear.index}
          tabIndex={-1}
          disabled={clear.disabled}
          onMouseEnter={() => setActive(clear.index)}
          onClick={clear.run}
          className={`mt-0.5 w-full rounded-sm border-t px-2 text-left text-xs ${variant === 'cell' ? 'py-1' : 'py-1.5'} ${clear.disabled ? 'cursor-default text-muted-foreground/60' : `text-primary ${active === clear.index ? 'bg-muted underline' : 'hover:underline'}`}`}
        >
          Clear all
        </button>
      )}
      {footer && (
        <button
          id={itemId(footer.index)}
          type='button'
          data-index={footer.index}
          tabIndex={-1}
          onMouseEnter={() => setActive(footer.index)}
          // Keeps focus on the list until the action takes it.
          onMouseDown={e => e.preventDefault()}
          onClick={footer.run}
          className={`mt-0.5 w-full rounded-sm border-t px-2 text-left text-xs text-primary ${variant === 'cell' ? 'py-1' : 'py-1.5'} ${active === footer.index ? 'bg-muted underline' : 'hover:underline'}`}
        >
          {footer.label}
        </button>
      )}
    </div>
  );
}
